import { ArrowLeft, Check, ClipboardCopy } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import type { EscalationRequest } from "@/lib/api";
import { logger } from "@/lib/logger";

import { TierBadge } from "./tier-badge";
import { escalationEmailBody, escalationEmailSubject, escalationTitle } from "./utils";

/**
 * One drafted brief, as the email a market would send.
 *
 * Subject and message are separate, and each copies on its own, because that is
 * how they get used — two fields in a mail client, not one block of text that
 * has to be pulled apart first.
 */
export function DraftView({
  escalation,
  onBack,
}: {
  escalation: EscalationRequest;
  onBack: () => void;
}) {
  const [copied, setCopied] = useState<"subject" | "body" | null>(null);
  const subject = escalationEmailSubject(escalation);
  const body = escalationEmailBody(escalation);

  const copy = async (which: "subject" | "body") => {
    try {
      await navigator.clipboard.writeText(which === "subject" ? subject : body);
      setCopied(which);
      window.setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      logger.error("Could not copy the draft", error);
    }
  };

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">
        <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
        Back to the conversation
      </Button>

      <header className="space-y-2">
        <p className="type-label text-muted">Draft for Strategic Comms</p>
        <div className="flex flex-wrap items-start gap-3">
          <h2 className="font-heading text-foreground flex-1 text-2xl font-bold">
            {escalationTitle(escalation)}
          </h2>
          <TierBadge tier={3} />
        </div>
        <p className="text-muted max-w-2xl text-sm leading-6">
          {escalation.kind === "missing_asset"
            ? "The library held nothing that answered the request, so the composer wrote the ask instead."
            : "This change needs a person's hands on the asset, so the composer drafted the ask instead."}{" "}
          Review the wording, then copy it into your mail client or ticketing tool.
        </p>
      </header>

      <div className="border-border bg-card overflow-hidden rounded-lg border">
        <div className="border-border flex items-center justify-between gap-3 border-b px-4 py-2.5">
          <div className="min-w-0">
            <p className="type-label text-muted">Subject</p>
            <p className="text-foreground truncate text-sm">{subject}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => void copy("subject")}>
            {copied === "subject" ? (
              <Check className="mr-1.5 h-3.5 w-3.5" />
            ) : (
              <ClipboardCopy className="mr-1.5 h-3.5 w-3.5" />
            )}
            {copied === "subject" ? "Copied" : "Copy"}
          </Button>
        </div>
        <div className="border-border flex items-center justify-between gap-3 border-b px-4 py-2.5">
          <span className="type-label text-muted">Message</span>
          <Button variant="outline" size="sm" onClick={() => void copy("body")}>
            {copied === "body" ? (
              <Check className="mr-1.5 h-3.5 w-3.5" />
            ) : (
              <ClipboardCopy className="mr-1.5 h-3.5 w-3.5" />
            )}
            {copied === "body" ? "Copied" : "Copy"}
          </Button>
        </div>
        <pre className="text-foreground overflow-x-auto px-4 py-4 text-sm leading-6 whitespace-pre-wrap">
          {body}
        </pre>
        <p className="text-muted border-border border-t px-4 py-2 text-xs">
          Reference {escalation.id}
        </p>
      </div>
    </div>
  );
}
